import { quotationFormConfig } from '../data/quotation/types';
import technical_specification, {
  getProductTechnicalTables,
  getGemaComponentTechnicalTables,
} from '../data/quotation/technical_specification';
import { getTechnicalTable } from '../data/quotation/types';
import { includes, GEMA_Automatic_System } from '../data/quotation/quotation_other_content';

/**
 * Get top-level options for a product (configKey from mainProductConfig e.g. 'oven', 'conveyor').
 */
export function getProductOptions(configKey) {
  const config = quotationFormConfig[configKey];
  return config?.options ?? [];
}

/**
 * Check if an option has nested options (needs another dropdown step).
 */
export function hasNestedOptions(option) {
  return option?.options && Array.isArray(option.options) && option.options.length > 0;
}

function toLabelValue(row) {
  const label = row?.Parameter ?? row?.parameter ?? row?.Component ?? row?.label ?? '';
  const value = row?.Value ?? row?.value ?? row?.Quantity ?? row?.quantity ?? '';
  return { label, value };
}

/**
 * Build technical table from conveyor spec option (chainPitch, hangingPitch, etc.) and parent (loadCapacityPerHook).
 */
function buildConveyorTechnicalData(selectedOption, parentOption) {
  const rows = [];
  if (parentOption?.loadCapacityPerHook) {
    rows.push({ label: 'Load Capacity per Hook', value: parentOption.loadCapacityPerHook });
  }
  if (selectedOption?.chainPitch) rows.push({ label: 'Chain Pitch', value: selectedOption.chainPitch });
  if (selectedOption?.hangingPitch) rows.push({ label: 'Hanging Pitch', value: selectedOption.hangingPitch });
  if (selectedOption?.horizontalBandRadius) rows.push({ label: 'Horizontal Band Radius', value: selectedOption.horizontalBandRadius });
  if (selectedOption?.verticalBendRadius) rows.push({ label: 'Vertical Bend Radius', value: selectedOption.verticalBendRadius });
  return {
    headers: ['Parameter', 'Value'],
    data: rows.length > 0 ? rows : [{ label: '', value: '' }],
  };
}

/**
 * Get initial technical data for a product.
 * selectionPath: array of { value, label } from dropdown selection.
 * selectedOption: the leaf option object (e.g. conveyor spec with chainPitch, hangingPitch).
 * parentOption: parent of selectedOption when nested (e.g. conveyor type with loadCapacityPerHook).
 */
export function getInitialTechnicalData(productKey, selectionPath = [], selectedOption = null, parentOption = null) {
  const isManualGunProduct = productKey === 'manual_gun';
  const hasManualGunInPath = selectionPath.some((s) => s.value === 'manual_gun');

  if (isManualGunProduct || hasManualGunInPath) {
    const tables = getProductTechnicalTables(technical_specification, 'manual_gun');
    const actualManualGun = includes?.actual_manual_gun;
    if (actualManualGun?.data) {
      tables.push({
        id: 'actual_manual_gun',
        label: actualManualGun.title || actualManualGun.text || 'Includes (Gema Manual Unit)',
        headers: actualManualGun.headers || ['Component', 'Quantity'],
        data: actualManualGun.data.map(toLabelValue),
      });
    }
    return { tables };
  }

  if (productKey === 'conveyor' && selectedOption && (selectedOption.chainPitch ?? selectedOption.hangingPitch)) {
    return buildConveyorTechnicalData(selectedOption, parentOption);
  }

  const selectedValue = typeof selectedOption === 'string' ? selectedOption : selectedOption?.value;
  const table = getTechnicalTable(technical_specification, productKey, selectedValue);
  if (!table || !table.data) return { headers: ['Parameter', 'Value'], data: [] };
  return {
    label: table.label,
    headers: table.headers || ['Parameter', 'Value'],
    data: table.data.map(toLabelValue),
  };
}

/**
 * Build display title from selection path (array of { label }).
 */
export function getSelectionTitle(selectionPath) {
  if (!selectionPath?.length) return '';
  return selectionPath.map((s) => s.label).join(' | ');
}

/**
 * Build initial data for Gema Automatic System – one card with all sub-components.
 * Each component has: key, label, quantity, technicalData (single or multi-table).
 */
export function getInitialGemaSystemData() {
  const source = GEMA_Automatic_System || {};
  return {
    components: Object.keys(source).map((key) => {
      const comp = source[key];
      const label = comp.title || comp.text || key.replace(/_/g, ' ');
      const tables = getGemaComponentTechnicalTables(source, key);
      const technicalData =
        tables.length > 0
          ? { tables }
          : { headers: ['Parameter', 'Value'], data: [] };
      return {
        key,
        label,
        quantity: 1,
        technicalData,
      };
    }),
  };
}
